import { Button, Checkbox, Intent } from "@blueprintjs/core";
import { ButtonsBloc, FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import {
  BroodRequestDto,
  BroodRequestPotentialFathersApi,
  PotentialFatherGridDto,
  PotentialFathersSearch,
} from "../../../../api";
import { CustomDataTable, DeleteButton, SaveButton } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useSaveApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { PotentialFatherSingleSelection } from "./PotentialFatherSingleSelection";

export interface IPotentialFathersValidationProps {
  broodRequestId: number;
  editMode: boolean;
  editable: boolean;
  refresh: () => void;
  requestData: BroodRequestDto;
  addPotentialFatherLoading: boolean;
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
`;

export const PotentialFathersValidation: React.FunctionComponent<IPotentialFathersValidationProps> = ({
  broodRequestId,
  editMode,
  editable,
  refresh,
  requestData,
  addPotentialFatherLoading,
}) => {
  const api = useApiService(BroodRequestPotentialFathersApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();

  const [authorizationList, setAuthorizationList] = React.useState<{
    [key: string]: boolean;
  }>({});

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [5],
    pageSize: 5,
  });

  const searchFunction = React.useCallback(
    async (sObj?: PotentialFathersSearch) => {
      const s = PotentialFathersSearch.fromJS({
        ...sObj,
        broodRequestId: broodRequestId,
        languageId: currentLanguage.languageId,
      });
      const result = await api.baseSearch(s);
      const tmp = {};
      result.results.map((e) => (tmp[e.dogId] = e.ownerApproval));
      setAuthorizationList({ ...tmp });
      return result;
    },
    [api, broodRequestId, currentLanguage.languageId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const changeAuthorization = React.useCallback(
    (dogId: number, value: boolean) => {
      const tmp = authorizationList;
      tmp[dogId] = value;
      console.log("tmp", tmp);
      setAuthorizationList({ ...tmp });
    },
    [authorizationList]
  );

  const authorizedButton = React.useCallback(
    (row: PotentialFatherGridDto) => {
      return (
        <Button
          minimal={true}
          intent={Intent.SUCCESS}
          disabled={!editable}
          icon={<i className="far fa-check-circle fa-lg" />}
          onClick={() => {
            changeAuthorization(row.dogId, true);
          }}
        />
      );
    },
    [changeAuthorization, editable]
  );

  const notAuthorizedButton = React.useCallback(
    (row: PotentialFatherGridDto) => {
      return (
        <Button
          minimal={true}
          intent={Intent.DANGER}
          disabled={!editable}
          icon={<i className="far fa-times-circle fa-lg" />}
          onClick={() => {
            changeAuthorization(row.dogId, false);
          }}
        />
      );
    },
    [changeAuthorization, editable]
  );

  const deletePotentialFather = React.useCallback(
    async (dogId: number) => {
      await api.deletePotentialFather(broodRequestId, dogId);
      search();
      refresh();
    },
    [api, broodRequestId, refresh, search]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: PotentialFatherGridDto) => (
          <ButtonContainer>
            <DeleteButton minimal={true} onDelete={() => deletePotentialFather(row.dogId)} />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Approval),
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        centered: true,
        alignment: "center",
        render: (row: PotentialFatherGridDto) => {
          if ((authorizationList[row.dogId] === null || authorizationList[row.dogId] === undefined) && editMode) {
            return (
              <ButtonContainer>
                {authorizedButton(row)}
                {notAuthorizedButton(row)}
              </ButtonContainer>
            );
          }
          return <Checkbox checked={authorizationList[row.dogId]} disabled={true} />;
        },
      },
      {
        header: () => t(ETLCodes.Name),
        fieldName: "name",
        render: (row: PotentialFatherGridDto) => (
          <Link target="_blank" to={`${ERoutes.dog}/${row.dogId}/detail/view`}>
            {row.name}
          </Link>
        ),
      },
    ],
    [authorizationList, authorizedButton, deletePotentialFather, editMode, notAuthorizedButton, t]
  );

  const { saving, saveItem } = useSaveApi(
    React.useMemo(
      () => ({
        saveApiFn: () => api.savePotentialFatherOwnerApproval(broodRequestId, authorizationList),
        onSaveFn: () => search(),
      }),
      [api, authorizationList, broodRequestId, search]
    )
  );

  const AddPotentialFatherComponent = React.useCallback(() => {
    return (
      !addPotentialFatherLoading && (
        <PotentialFatherSingleSelection
          broodRequestId={broodRequestId}
          linkedFatherIds={requestData?.potentialFatherIds}
          editMode={editMode}
          motherId={requestData?.motherId}
          onFatherAdded={() => {
            search();
            refresh();
          }}
        />
      )
    );
  }, [
    addPotentialFatherLoading,
    broodRequestId,
    editMode,
    refresh,
    requestData?.motherId,
    requestData?.potentialFatherIds,
    search,
  ]);

  return (
    <>
      <FieldSet title={t(ETLCodes.PotentialFathersOwnerApprovals)}>
        <AddPotentialFatherComponent />
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
        {editMode && editable && (
          <ButtonsBloc style={{ float: "right" }}>
            <SaveButton
              minimal={false}
              onClick={saveItem}
              loading={saving}
              disabled={!tableState.data.some((e) => e.ownerApproval === null)}
            />
          </ButtonsBloc>
        )}
      </FieldSet>
    </>
  );
};
